/**
 * T135 client — Expense attachment list hook.
 *
 * Wraps the backend contract from T119 (`apps/api/src/routes/expense-attachments.ts`):
 *   - `GET /expenses/:id/attachments` → list
 *   - download URL is session-bound at `/expense-attachments/:id`
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { expenseAttachmentKeys } from './keys';
import { useActiveTenantId } from '../../hooks/useTenantContext';
import type { ExpenseAttachmentDto } from '@armard/shared';

const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

export const attachmentsApi = {
  list: (expenseId: string) =>
    api.get<{ items: ExpenseAttachmentDto[] }>(
      `/expenses/${expenseId}/attachments`
    ),
};

export function useExpenseAttachments(expenseId: string | undefined) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: expenseAttachmentKeys.forExpense(tenantId, expenseId!),
    queryFn: () => attachmentsApi.list(expenseId!),
    enabled: !!expenseId && tenantId !== null,
  });
}

export function attachmentDownloadUrl(attachmentId: string): string {
  return `${API_BASE_URL}/expense-attachments/${attachmentId}`;
}
